import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ActionCard from 'components/Common/Cards/ActionCard';
import Button, { ButtonType } from 'components/Button';
import { getLoanGoal } from 'selectors/getLoanGoal';
import { getApplicationData } from 'selectors/getApplicationData';
import { DebtSummary, HardOfferData } from 'handlers/applicationData';
import { normalizeDebtSummary } from 'components/FinancialCheckup/YourFinances/yourFinancesUtils';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { isProbableLoanOffer } from 'components/FinancialCheckup/loanOfferAccess';
import ProbableOfferMark from 'components/FinancialCheckup/ProbableOfferMark/ProbableOfferMark';
import usePursueLoanOffer from 'hooks/usePursueLoanOffer';
import { AICoachGoalType } from 'enums/AICoach';
import { RoutePath } from 'enums/Routes';
import { useNavigate } from 'hooks/useNavigate';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useOpenAiCoachConversationChat from 'hooks/useOpenAiCoachConversationChat';
import { getGoalProgress, getNextSteps, startScoreImprovementPlan } from 'thunks';
import { OFFER_AVAILABLE_FROM_DASHBOARD_STATE } from 'utils/navigationStates';
import { hasPrimaryGoal } from 'utils/aiCoachUtils';

import { ReactComponent as UpwardTrendIcon } from 'images/upward-trend-icon.svg';
import { ReactComponent as DownwardTrendIcon } from 'images/downward-trend-icon.svg';
import { ReactComponent as WalletIcon } from 'images/repayment-items/wallet-icon.svg';
import { ReactComponent as SparklesIcon } from 'images/sparkles-icon.svg';
import PoweredByEquifax, {
  PoweredByEquifaxTone,
} from 'components/FinancialCheckup/FinancialDashboard/PoweredByEquifax';

import {
  getConsolidatableMaxRemainingMonths,
  getSelectedAICoachGoal,
} from 'components/FinancialCheckup/FinancialDashboard/YourGoalSection/getYourGoalTrajectory';

import TrajectoryComparison from './TrajectoryComparison';
import {
  AcceleratePrimaryAction,
  resolveAccelerateYourGoalsCard,
  resolveTrajectoryComparison,
} from './resolveAccelerateYourGoalsCard';

import styles from './AccelerateYourGoalsSection.module.scss';

const GOAL_ICONS: Record<AICoachGoalType, React.FC<React.SVGProps<SVGSVGElement>>> = {
  [AICoachGoalType.PayDownCreditCardDebt]: DownwardTrendIcon,
  [AICoachGoalType.ImproveCreditScore]: DownwardTrendIcon,
  [AICoachGoalType.BuildEmergencyFund]: UpwardTrendIcon,
  [AICoachGoalType.PayDownStudentLoanDebt]: UpwardTrendIcon,
  [AICoachGoalType.SaveForSpecificExpense]: UpwardTrendIcon,
  [AICoachGoalType.Other]: UpwardTrendIcon,
  [AICoachGoalType.SaveForRetirement]: UpwardTrendIcon,
};

const AccelerateYourGoalsSection: React.FC = () => {
  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const navigate = useNavigate();
  const openAiCoachConversationChat = useOpenAiCoachConversationChat();
  const { pursueLoan, isPursuingLoan } = usePursueLoanOffer();

  const { aiCoachGoals, isLoading: isLoadingGoals, isStartingScoreImprovementPlan } = useSelector(getLoanGoal);
  const { application } = useSelector(getApplicationData);
  const selectedGoal = getSelectedAICoachGoal(aiCoachGoals);

  if (
    !application ||
    isLoadingGoals ||
    ![
      ApplicationStatusName.OfferAvailable,
      ApplicationStatusName.OfferPresented,
      ApplicationStatusName.Rejected,
    ].includes(application.status)
  )
    return null;

  const isRejected = application.status === ApplicationStatusName.Rejected;
  const isProbableOffer = isProbableLoanOffer(application.status);
  const isCreditScoreWithOffer = selectedGoal?.type === AICoachGoalType.ImproveCreditScore && !isRejected;

  if (hasPrimaryGoal(aiCoachGoals) && !isCreditScoreWithOffer) return null;

  const selectedAccelerateGoal = isCreditScoreWithOffer
    ? { ...selectedGoal, type: AICoachGoalType.PayDownCreditCardDebt }
    : selectedGoal;
  const hardOffer = (application.hardOffer ?? {}) as Partial<HardOfferData>;
  const rawDebtSummary = application.debtSummary as DebtSummary | undefined;
  const debtSummary = rawDebtSummary ? normalizeDebtSummary(rawDebtSummary) : undefined;
  const cardContent = resolveAccelerateYourGoalsCard(
    isRejected,
    hardOffer,
    application.optimalPathPlan?.scoreImprovement,
    debtSummary,
    selectedGoal,
  );

  if (!cardContent) return null;

  const currentMonths = getConsolidatableMaxRemainingMonths(debtSummary);
  const planneryMonths = hardOffer.planneryLoan?.term ?? application.loanTermInMonths;
  const trajectory = resolveTrajectoryComparison(
    hardOffer,
    currentMonths,
    planneryMonths,
    debtSummary,
    selectedAccelerateGoal,
    isRejected,
  );

  const GoalIcon = selectedAccelerateGoal ? GOAL_ICONS[selectedAccelerateGoal.type] : WalletIcon;

  const handlePrimaryClick = async () => {
    if (cardContent.primaryAction === AcceleratePrimaryAction.EnrollScoreIQ) {
      try {
        await dispatchWithUnwrap(startScoreImprovementPlan(application.id));
        if (application.borrowerId) {
          dispatch(getGoalProgress(application.borrowerId));
          dispatch(getNextSteps(application.borrowerId));
        }
      } catch (error) {
        analytics.track('Failed to start score improvement plan', { error });
      }
      return;
    }

    if (isProbableOffer) {
      await pursueLoan();
      return;
    }

    navigate(RoutePath.OfferAvailable, OFFER_AVAILABLE_FROM_DASHBOARD_STATE);
  };

  return (
    <div className={styles.nextStepSection}>
      <p className={styles.sectionLabel}>Accelerate your goals</p>
      <ActionCard>
        <ActionCard.Row className={styles.titleRow}>
          <div className={styles.iconCircle}>
            <GoalIcon />
          </div>
          <div className={styles.titleBlock}>
            <ActionCard.Title className={styles.title}>
              {cardContent.title.endsWith('©') ? (
                <>
                  {cardContent.title.slice(0, -1)}
                  <sup className={styles.copyright}>©</sup>
                </>
              ) : (
                cardContent.title
              )}
            </ActionCard.Title>
            {cardContent.showScoreIQAttribution && <PoweredByEquifax tone={PoweredByEquifaxTone.OnDark} />}
          </div>
        </ActionCard.Row>
        {cardContent.subtitle && <ActionCard.Description>{cardContent.subtitle}</ActionCard.Description>}
        {cardContent.bullets && cardContent.bullets.length > 0 && (
          <div className={styles.bulletsContainer}>
            <ol className={styles.bullets}>
              {cardContent.bullets.map((bullet) => (
                <li key={bullet} className={styles.bullet}>
                  {bullet}
                </li>
              ))}
            </ol>
          </div>
        )}
        {trajectory && (
          <TrajectoryComparison currentValue={trajectory.currentValue} planneryValue={trajectory.planneryValue} />
        )}
        <Button
          type={ButtonType.Inverted}
          onClick={handlePrimaryClick}
          isLoading={isStartingScoreImprovementPlan || isPursuingLoan}
        >
          {cardContent.primaryButtonText}
          {isProbableOffer && cardContent.primaryAction === AcceleratePrimaryAction.SeeMyPlan && <ProbableOfferMark />}
        </Button>
        <Button type={ButtonType.Transparent} className={styles.explainButton} onClick={openAiCoachConversationChat}>
          <SparklesIcon className={styles.sparklesIcon} />
          EXPLAIN THIS GUIDANCE
        </Button>
      </ActionCard>
    </div>
  );
};

export default AccelerateYourGoalsSection;
